// Message classifier tools for the analytics chat: a tenant admin trains a
// classifier for a yes-or-no question about one kind of message part (the
// user's text, the agent's text or thinking, a tool call or a tool's result),
// follows the training, and saves the result for the findings verifier's
// find_parts. The server holds every rule (admins only, one live training per
// label, saving only a ready training); these tools speak the API and keep
// protobuf UUIDs base64 inside this module.

import { Type } from "@earendil-works/pi-ai";
import { idForPath, restDelete, restGet, restPost } from "./restClient.js";
import { bytesBase64ToUuid, enumSuffix, pick, uuidToBytesBase64 } from "./reviewClient.js";

const STATUS_PREFIX = "MESSAGE_CLASSIFIER_TRAINING_STATUS_";
// The kinds of part a classifier asks about (the server's MESSAGE_PART_KINDS).
export const PART_KINDS = ["user_text", "assistant_text", "thinking", "tool_call", "tool_result"];
const FINISHED = new Set(["ready", "refused", "failed", "saved"]);
// How often a wait re-reads a running training, and the longest one tool call waits.
const POLL_MS = 5000;
const MAX_WAIT_SECONDS = 120;

export function adaptTraining(t) {
    return {
        trainingId: bytesBase64ToUuid(pick(t, "training_id", "trainingId")),
        label: t?.label || "",
        question: t?.question || "",
        partKind: pick(t, "part_kind", "partKind") || "",
        status: enumSuffix(STATUS_PREFIX, t?.status),
        stage: t?.stage || "",
        labelled: Number(t?.labelled) || 0,
        yes: Number(t?.yes) || 0,
        // null when too few of the randomly sampled parts answered yes to estimate it
        estimatedAuc: Number(pick(t, "estimated_auc", "estimatedAuc")) || null,
        positives: Number(t?.positives) || 0,
        negatives: Number(t?.negatives) || 0,
        enriched: Boolean(t?.enriched),
        examples: (t?.examples || []).map((e) => ({
            traceId: bytesBase64ToUuid(pick(e, "trace_id", "traceId")),
            messageId: bytesBase64ToUuid(pick(e, "message_id", "messageId")),
            score: Number(e?.score) || 0,
            text: e?.text || "",
        })),
        error: t?.error || "",
        // "chat" (an admin) or "verifier" (a findings verification job that used it for itself)
        origin: t?.origin || "",
        createdAt: pick(t, "created_at", "createdAt") || null,
        updatedAt: pick(t, "updated_at", "updatedAt") || null,
    };
}

export function adaptClassifier(c) {
    return {
        classifierId: bytesBase64ToUuid(pick(c, "classifier_id", "classifierId")),
        label: c?.label || "",
        question: c?.question || "",
        partKind: pick(c, "part_kind", "partKind") || "",
        builtIn: Boolean(pick(c, "built_in", "builtIn")),
        estimatedAuc: Number(pick(c, "estimated_auc", "estimatedAuc")) || null,
        positives: Number(c?.positives) || 0,
        negatives: Number(c?.negatives) || 0,
        updatedAt: pick(c, "updated_at", "updatedAt") || null,
    };
}

function pathId(uuid, what) {
    const b64 = uuidToBytesBase64(uuid);
    if (!b64) throw new Error(`${what} is not a uuid`);
    return idForPath(b64);
}

export async function startTraining({ label, question, partKind }) {
    return adaptTraining(await restPost("/v1/message-classifier-trainings", { label, question, part_kind: partKind }));
}

export async function getTraining(trainingId, { signal } = {}) {
    return adaptTraining(await restGet(`/v1/message-classifier-trainings/${pathId(trainingId, "training_id")}`, null, { signal }));
}

export async function listTrainings({ signal } = {}) {
    const body = await restGet("/v1/message-classifier-trainings", null, { signal });
    return (body?.trainings || []).map(adaptTraining);
}

export async function saveTraining(trainingId) {
    return adaptClassifier(await restPost(`/v1/message-classifier-trainings/${pathId(trainingId, "training_id")}:save`, {}));
}

export async function listClassifiers({ signal } = {}) {
    const body = await restGet("/v1/message-classifiers", null, { signal });
    return (body?.classifiers || []).map(adaptClassifier);
}

export async function deleteClassifier(classifierId) {
    await restDelete(`/v1/message-classifiers/${pathId(classifierId, "classifier_id")}`);
}

// The server's own words for a refusal (an unfinished training of the label,
// a training that is not ready, a missing permission) reach the model.
async function answer(call) {
    try {
        const result = await call();
        return { content: [{ type: "text", text: JSON.stringify(result) }], details: result };
    } catch (error) {
        throw new Error(error?.serverMessage || error?.message || String(error));
    }
}

const sleepFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function createMessageClassifierTools({ sleep = sleepFor } = {}) {
    const list = {
        name: "list_message_classifiers",
        label: "Message classifiers",
        description: "The classifiers the findings verifier can rank this tenant's message parts by (its find_parts): the tenant's own, then the built-in ones, each with its question, the kind of part it asks about, and its estimated AUC.",
        parameters: Type.Object({}),
        execute: (_id, _args, signal) => answer(async () => ({ classifiers: await listClassifiers({ signal }) })),
    };
    const train = {
        name: "train_message_classifier",
        label: "Train message classifier",
        description: "Start training a classifier for a yes-or-no question about one kind of message part in this tenant's agent sessions: the user's text (\"Does the user ask the agent to deploy to production?\"), the agent's text or thinking, a tool call (\"Does the call deploy to production?\") or a tool's result. A worker samples parts of that kind from the sessions reviewed in the last 30 days, has the self-hosted judge answer the question for each, and fits a classifier on the judge's per-part codes; it takes minutes. Returns the training: follow it with message_classifier_training. One training per label at a time, and at most 10 a day for the tenant; retraining a label replaces the tenant's classifier of that label once saved.",
        parameters: Type.Object({
            label: Type.String({ description: "The classifier's name: lowercase letters, digits and underscores, starting with a letter, at most 63 characters, such as deploy_prod. The findings verifier asks for it by this name." }),
            question: Type.String({ description: "The yes-or-no question about one part, in plain words, saying what counts as yes and what does not." }),
            part_kind: Type.Union(PART_KINDS.map((k) => Type.Literal(k)), { description: "The kind of part the question is about: user_text (what the user wrote), assistant_text (what the agent wrote), thinking (the agent's thinking), tool_call (a call the agent made) or tool_result (a tool's output)." }),
        }),
        execute: (_id, { label, question, part_kind }) => answer(() => startTraining({ label, question, partKind: part_kind })),
    };
    const trainings = {
        name: "list_message_classifier_trainings",
        label: "Classifier trainings",
        description: "The tenant's newest classifier trainings with their status, AUC and origin: an admin's from this chat (origin chat), or the findings verifier's (origin verifier), which it trained for one job when no classifier asked its question. A ready verifier training becomes a classifier for every job only when an admin saves it with save_message_classifier, after seeing its question, AUC and example turns.",
        parameters: Type.Object({}),
        execute: (_id, _args, signal) => answer(async () => ({ trainings: await listTrainings({ signal }) })),
    };
    const follow = {
        name: "message_classifier_training",
        label: "Classifier training",
        description: "A training's progress, waiting up to wait_seconds (at most 120) for it to finish. Ready: its estimated AUC, how well it ranks parts the way the judge answered the question (cross-validated across sessions on randomly sampled parts; an estimate, null when too few of them answered yes), how many labelled parts it was fitted on, and the best-scoring parts nobody labelled; show the user those parts and the AUC and ask before saving. Refused or failed: the reason. Still running: call again.",
        parameters: Type.Object({
            training_id: Type.String({ description: "The training's id." }),
            wait_seconds: Type.Optional(Type.Number({ description: "How long to wait for it to finish, 0 to 120; 60 when left out." })),
        }),
        execute: (_id, { training_id, wait_seconds = 60 }, signal, onUpdate) => answer(async () => {
            const deadline = Date.now() + Math.min(MAX_WAIT_SECONDS, Math.max(0, Number(wait_seconds) || 0)) * 1000;
            let training = await getTraining(training_id, { signal });
            while (!FINISHED.has(training.status) && Date.now() < deadline && !signal?.aborted) {
                onUpdate?.({ content: [{ type: "text", text: JSON.stringify(training) }], details: training });
                await sleep(POLL_MS);
                training = await getTraining(training_id, { signal });
            }
            return training;
        }),
    };
    const save = {
        name: "save_message_classifier",
        label: "Save message classifier",
        description: "Save a ready training's classifier for the tenant, replacing its classifier of the same label. The findings verifier can rank parts by it from its next job. A tenant keeps at most 50 classifiers for the current judge; past that, one must be deleted first. Only after the user has seen the training's AUC and example parts and agreed to save it.",
        parameters: Type.Object({ training_id: Type.String({ description: "The ready training's id." }) }),
        execute: (_id, { training_id }) => answer(() => saveTraining(training_id)),
    };
    const remove = {
        name: "delete_message_classifier",
        label: "Delete message classifier",
        description: "Delete one of the tenant's own classifiers; built-in ones cannot be deleted. Only when the user has asked to delete that classifier.",
        parameters: Type.Object({ classifier_id: Type.String({ description: "The classifier's id, from list_message_classifiers." }) }),
        execute: (_id, { classifier_id }) => answer(async () => {
            await deleteClassifier(classifier_id);
            return { deleted: classifier_id };
        }),
    };
    return [list, trainings, train, follow, save, remove];
}
